/**
 * Component: UserManagementPage
 * SpecReferenceID: AUTH-V1
 * Requirements: AUTH-V1-R15
 *
 * Admin user management page for listing, creating, editing, and
 * deactivating users via the /admin/users API.
 * Accessibility: WCAG 2.1 AA compliant
 */

import React, { useState, useCallback, useEffect, useMemo } from 'react';
import {
  Users,
  Plus,
  Pencil,
  Loader2,
  AlertCircle,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
  ChevronLeft,
  ChevronRight,
  ShieldCheck,
  ShieldOff,
} from 'lucide-react';

import api, { getErrorMessage } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

// ── Types ────────────────────────────────────────────────────────────────────

interface UserResponse {
  id: string;
  idp_user_id: string;
  email: string;
  display_name: string;
  role: string;
  is_active: boolean;
  last_login_at: string | null;
  created_at: string;
  updated_at: string;
}

interface UserListResponse {
  users: UserResponse[];
  total: number;
  page: number;
  page_size: number;
}

interface UserFormData {
  idp_user_id: string;
  email: string;
  display_name: string;
  role: string;
  password: string;
}

type SortField = 'email' | 'display_name' | 'role' | 'is_active' | 'last_login_at';
type SortDirection = 'asc' | 'desc';

const VALID_ROLES = ['user', 'reviewer', 'admin'] as const;
const PAGE_SIZES = [10, 25, 50] as const;

const INITIAL_FORM_DATA: UserFormData = {
  idp_user_id: '',
  email: '',
  display_name: '',
  role: 'user',
  password: '',
};

// ── Helpers ──────────────────────────────────────────────────────────────────

function formatDateTime(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function roleBadgeVariant(role: string): 'default' | 'secondary' | 'outline' {
  switch (role) {
    case 'admin':
      return 'default';
    case 'reviewer':
      return 'secondary';
    default:
      return 'outline';
  }
}

// ── Component ────────────────────────────────────────────────────────────────

export function UserManagementPage(): React.ReactElement {
  // List state
  const [users, setUsers] = useState<UserResponse[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(25);
  const [activeOnly, setActiveOnly] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Sort state
  const [sortField, setSortField] = useState<SortField>('email');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<UserResponse | null>(null);
  const [formData, setFormData] = useState<UserFormData>(INITIAL_FORM_DATA);
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Confirm deactivate/activate state
  const [confirmUser, setConfirmUser] = useState<UserResponse | null>(null);
  const [confirmLoading, setConfirmLoading] = useState(false);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  // ── Fetch users ──────────────────────────────────────────────────────────

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get<UserListResponse>('/admin/users', {
        params: { page, page_size: pageSize, active_only: activeOnly },
      });
      setUsers(response.data.users);
      setTotal(response.data.total);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, activeOnly]);

  useEffect(() => {
    void fetchUsers();
  }, [fetchUsers]);

  // Clear success message after a delay
  useEffect(() => {
    if (!successMessage) return;
    const timer = setTimeout(() => setSuccessMessage(null), 4000);
    return () => clearTimeout(timer);
  }, [successMessage]);

  // ── Sorting ──────────────────────────────────────────────────────────────

  const handleSort = useCallback((field: SortField) => {
    setSortField((prev) => {
      if (prev === field) {
        setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
        return prev;
      }
      setSortDirection('asc');
      return field;
    });
  }, []);

  const sortedUsers = useMemo(() => {
    const sorted = [...users].sort((a, b) => {
      let aVal: string | boolean | null;
      let bVal: string | boolean | null;

      switch (sortField) {
        case 'email':
          aVal = a.email.toLowerCase();
          bVal = b.email.toLowerCase();
          break;
        case 'display_name':
          aVal = a.display_name.toLowerCase();
          bVal = b.display_name.toLowerCase();
          break;
        case 'role':
          aVal = a.role;
          bVal = b.role;
          break;
        case 'is_active':
          aVal = a.is_active;
          bVal = b.is_active;
          break;
        case 'last_login_at':
          aVal = a.last_login_at ?? '';
          bVal = b.last_login_at ?? '';
          break;
        default:
          return 0;
      }

      if (typeof aVal === 'boolean') {
        return sortDirection === 'asc'
          ? (aVal === bVal ? 0 : aVal ? -1 : 1)
          : (aVal === bVal ? 0 : aVal ? 1 : -1);
      }

      const cmp = String(aVal).localeCompare(String(bVal));
      return sortDirection === 'asc' ? cmp : -cmp;
    });
    return sorted;
  }, [users, sortField, sortDirection]);

  // ── Sort header button ───────────────────────────────────────────────────

  const SortButton = useCallback(
    ({ field, label }: { field: SortField; label: string }) => {
      const isActive = sortField === field;
      const Icon = isActive
        ? sortDirection === 'asc'
          ? ArrowUp
          : ArrowDown
        : ArrowUpDown;

      return (
        <Button
          variant="ghost"
          size="sm"
          className="-ml-3 h-8"
          onClick={() => handleSort(field)}
          aria-label={`Sort by ${label}${isActive ? `, currently ${sortDirection === 'asc' ? 'ascending' : 'descending'}` : ''}`}
        >
          {label}
          <Icon className="ml-1 h-3.5 w-3.5" aria-hidden="true" />
        </Button>
      );
    },
    [sortField, sortDirection, handleSort],
  );

  // ── Dialog handlers ──────────────────────────────────────────────────────

  const openCreateDialog = useCallback(() => {
    setEditingUser(null);
    setFormData(INITIAL_FORM_DATA);
    setFormError(null);
    setDialogOpen(true);
  }, []);

  const openEditDialog = useCallback((user: UserResponse) => {
    setEditingUser(user);
    setFormData({
      idp_user_id: user.idp_user_id,
      email: user.email,
      display_name: user.display_name,
      role: user.role,
      password: '',
    });
    setFormError(null);
    setDialogOpen(true);
  }, []);

  const handleDialogClose = useCallback((open: boolean) => {
    if (!open) {
      setDialogOpen(false);
      setEditingUser(null);
      setFormError(null);
    }
  }, []);

  // ── Form validation ──────────────────────────────────────────────────────

  const validateForm = useCallback((): string | null => {
    if (!editingUser) {
      // Create mode — all fields required except password
      if (!formData.idp_user_id.trim()) return 'IDP User ID is required.';
      if (formData.idp_user_id.length > 255) return 'IDP User ID must be 255 characters or fewer.';
    }
    if (!formData.email.trim()) return 'Email is required.';
    if (formData.email.length < 3 || formData.email.length > 320) return 'Email must be between 3 and 320 characters.';
    if (!formData.display_name.trim()) return 'Display name is required.';
    if (formData.display_name.length > 255) return 'Display name must be 255 characters or fewer.';
    if (!VALID_ROLES.includes(formData.role as typeof VALID_ROLES[number])) return 'Invalid role selected.';
    if (formData.password && (formData.password.length < 8 || formData.password.length > 128)) {
      return 'Password must be between 8 and 128 characters.';
    }
    return null;
  }, [formData, editingUser]);

  // ── Submit handler ───────────────────────────────────────────────────────

  const handleSubmit = useCallback(async () => {
    const validationError = validateForm();
    if (validationError) {
      setFormError(validationError);
      return;
    }

    setSubmitting(true);
    setFormError(null);

    try {
      if (editingUser) {
        // Update
        const payload: Record<string, unknown> = {
          display_name: formData.display_name,
          role: formData.role,
        };
        if (formData.password) {
          payload['password'] = formData.password;
        }
        await api.patch(`/admin/users/${editingUser.id}`, payload);
        setSuccessMessage(`User "${formData.display_name}" updated successfully.`);
      } else {
        // Create
        const payload: Record<string, unknown> = {
          idp_user_id: formData.idp_user_id,
          email: formData.email,
          display_name: formData.display_name,
          role: formData.role,
        };
        if (formData.password) {
          payload['password'] = formData.password;
        }
        await api.post('/admin/users', payload);
        setSuccessMessage(`User "${formData.display_name}" created successfully.`);
      }

      setDialogOpen(false);
      setEditingUser(null);
      void fetchUsers();
    } catch (err) {
      setFormError(getErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  }, [editingUser, formData, validateForm, fetchUsers]);

  // ── Deactivate / Activate ────────────────────────────────────────────────

  const handleToggleActive = useCallback(async () => {
    if (!confirmUser) return;
    setConfirmLoading(true);

    try {
      if (confirmUser.is_active) {
        await api.delete(`/admin/users/${confirmUser.id}`);
        setSuccessMessage(`User "${confirmUser.display_name}" deactivated.`);
      } else {
        await api.patch(`/admin/users/${confirmUser.id}`, { is_active: true });
        setSuccessMessage(`User "${confirmUser.display_name}" activated.`);
      }
      setConfirmUser(null);
      void fetchUsers();
    } catch (err) {
      setError(getErrorMessage(err));
      setConfirmUser(null);
    } finally {
      setConfirmLoading(false);
    }
  }, [confirmUser, fetchUsers]);

  // ── Pagination ───────────────────────────────────────────────────────────

  const handlePageSizeChange = useCallback((value: string) => {
    setPageSize(Number(value));
    setPage(1);
  }, []);

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <Users className="h-6 w-6" aria-hidden="true" />
            User Management
          </h1>
          <p className="text-muted-foreground mt-1">
            Manage user accounts, roles, and access.
          </p>
        </div>
        <Button onClick={openCreateDialog} aria-label="Add new user">
          <Plus className="h-4 w-4 mr-2" aria-hidden="true" />
          Add User
        </Button>
      </div>

      {/* Alerts */}
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" aria-hidden="true" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      {successMessage && (
        <Alert>
          <AlertDescription>{successMessage}</AlertDescription>
        </Alert>
      )}

      {/* Users table card */}
      <Card>
        <CardHeader className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <CardTitle className="text-lg">Users</CardTitle>
          <div className="flex items-center gap-3">
            <Label htmlFor="active-filter" className="text-sm whitespace-nowrap">
              Show inactive
            </Label>
            <input
              id="active-filter"
              type="checkbox"
              checked={!activeOnly}
              onChange={(e) => {
                setActiveOnly(!e.target.checked);
                setPage(1);
              }}
              className="h-4 w-4 rounded border-gray-300"
              aria-label="Show inactive users"
            />
            <Select value={String(pageSize)} onValueChange={handlePageSizeChange}>
              <SelectTrigger className="w-[100px]" aria-label="Rows per page">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PAGE_SIZES.map((size) => (
                  <SelectItem key={size} value={String(size)}>
                    {size} rows
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12" aria-label="Loading users">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" aria-hidden="true" />
              <span className="sr-only">Loading users…</span>
            </div>
          ) : sortedUsers.length === 0 ? (
            <p className="text-center text-muted-foreground py-12">
              No users found.
            </p>
          ) : (
            <>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>
                        <SortButton field="email" label="Email" />
                      </TableHead>
                      <TableHead>
                        <SortButton field="display_name" label="Display Name" />
                      </TableHead>
                      <TableHead>
                        <SortButton field="role" label="Role" />
                      </TableHead>
                      <TableHead>
                        <SortButton field="is_active" label="Status" />
                      </TableHead>
                      <TableHead>
                        <SortButton field="last_login_at" label="Last Login" />
                      </TableHead>
                      <TableHead className="text-right">
                        <span className="sr-only">Actions</span>
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sortedUsers.map((user) => (
                      <TableRow key={user.id}>
                        <TableCell className="font-medium">{user.email}</TableCell>
                        <TableCell>{user.display_name}</TableCell>
                        <TableCell>
                          <Badge variant={roleBadgeVariant(user.role)}>
                            {user.role}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge variant={user.is_active ? 'default' : 'destructive'}>
                            {user.is_active ? 'Active' : 'Inactive'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-muted-foreground text-sm">
                          {formatDateTime(user.last_login_at)}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => openEditDialog(user)}
                              aria-label={`Edit user ${user.display_name}`}
                            >
                              <Pencil className="h-4 w-4" aria-hidden="true" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => setConfirmUser(user)}
                              aria-label={`${user.is_active ? 'Deactivate' : 'Activate'} user ${user.display_name}`}
                            >
                              {user.is_active ? (
                                <ShieldOff className="h-4 w-4" aria-hidden="true" />
                              ) : (
                                <ShieldCheck className="h-4 w-4" aria-hidden="true" />
                              )}
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              <div className="flex items-center justify-between pt-4">
                <p className="text-sm text-muted-foreground">
                  Showing {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, total)} of {total} users
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page <= 1}
                    onClick={() => setPage((p) => p - 1)}
                    aria-label="Previous page"
                  >
                    <ChevronLeft className="h-4 w-4" aria-hidden="true" />
                  </Button>
                  <span className="text-sm text-muted-foreground">
                    Page {page} of {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page >= totalPages}
                    onClick={() => setPage((p) => p + 1)}
                    aria-label="Next page"
                  >
                    <ChevronRight className="h-4 w-4" aria-hidden="true" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Create / Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={handleDialogClose}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>{editingUser ? 'Edit User' : 'Create User'}</DialogTitle>
            <DialogDescription>
              {editingUser
                ? `Update details for ${editingUser.email}.`
                : 'Add a new user account.'}
            </DialogDescription>
          </DialogHeader>

          {formError && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" aria-hidden="true" />
              <AlertDescription>{formError}</AlertDescription>
            </Alert>
          )}

          <div className="grid gap-4 py-4">
            {!editingUser && (
              <div className="grid gap-2">
                <Label htmlFor="idp_user_id">IDP User ID</Label>
                <Input
                  id="idp_user_id"
                  value={formData.idp_user_id}
                  onChange={(e) => setFormData((f) => ({ ...f, idp_user_id: e.target.value }))}
                  placeholder="Identity provider subject"
                  maxLength={255}
                  required
                />
              </div>
            )}
            <div className="grid gap-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData((f) => ({ ...f, email: e.target.value }))}
                placeholder="user@example.com"
                maxLength={320}
                disabled={!!editingUser}
                required
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="display_name">Display Name</Label>
              <Input
                id="display_name"
                value={formData.display_name}
                onChange={(e) => setFormData((f) => ({ ...f, display_name: e.target.value }))}
                placeholder="Jane Doe"
                maxLength={255}
                required
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="role">Role</Label>
              <Select
                value={formData.role}
                onValueChange={(value) => setFormData((f) => ({ ...f, role: value }))}
              >
                <SelectTrigger id="role" aria-label="Select role">
                  <SelectValue placeholder="Select role" />
                </SelectTrigger>
                <SelectContent>
                  {VALID_ROLES.map((role) => (
                    <SelectItem key={role} value={role}>
                      {role.charAt(0).toUpperCase() + role.slice(1)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="password">
                Password{editingUser ? ' (leave blank to keep current)' : ' (optional)'}
              </Label>
              <Input
                id="password"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData((f) => ({ ...f, password: e.target.value }))}
                placeholder={editingUser ? '••••••••' : 'Min 8 characters'}
                minLength={8}
                maxLength={128}
                autoComplete="new-password"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={submitting}>
              Cancel
            </Button>
            <Button onClick={() => { void handleSubmit(); }} disabled={submitting}>
              {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" aria-hidden="true" />}
              {editingUser ? 'Save Changes' : 'Create User'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm Deactivate / Activate Dialog */}
      <Dialog open={!!confirmUser} onOpenChange={(open) => { if (!open) setConfirmUser(null); }}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>
              {confirmUser?.is_active ? 'Deactivate User' : 'Activate User'}
            </DialogTitle>
            <DialogDescription>
              {confirmUser?.is_active
                ? `Are you sure you want to deactivate "${confirmUser?.display_name}"? They will no longer be able to log in.`
                : `Are you sure you want to activate "${confirmUser?.display_name}"? They will be able to log in again.`}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmUser(null)} disabled={confirmLoading}>
              Cancel
            </Button>
            <Button
              variant={confirmUser?.is_active ? 'destructive' : 'default'}
              onClick={() => { void handleToggleActive(); }}
              disabled={confirmLoading}
            >
              {confirmLoading && <Loader2 className="h-4 w-4 mr-2 animate-spin" aria-hidden="true" />}
              {confirmUser?.is_active ? 'Deactivate' : 'Activate'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
